import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { DealerPortalUser, DealerProfileUpdateInput, DealerRegistrationInput } from '@/lib/domain';
import { getBackendConfigError } from '@/lib/api/client';
import {
  fetchCurrentDealerProfile,
  registerDealer,
  sendPasswordReset,
  signInDealer,
  signOutCurrentUser,
  updateCurrentUserPassword,
  updateDealerProfile,
} from '@/lib/api/profiles';

interface ActionResult {
  ok: boolean;
  error?: string;
  message?: string;
}

interface DealerAuthContextValue {
  dealer: DealerPortalUser | null;
  isLoggedIn: boolean;
  loading: boolean;
  configError: string;
  login: (email: string, password: string) => Promise<ActionResult>;
  register: (input: DealerRegistrationInput) => Promise<ActionResult>;
  logout: () => Promise<void>;
  sendResetPassword: (email: string) => Promise<ActionResult>;
  updateProfile: (input: DealerProfileUpdateInput) => Promise<ActionResult>;
  updatePassword: (newPassword: string) => Promise<ActionResult>;
  refresh: () => Promise<void>;
}

const defaultConfigError = getBackendConfigError() || '';

const DealerAuthContext = createContext<DealerAuthContextValue>({
  dealer: null,
  isLoggedIn: false,
  loading: false,
  configError: defaultConfigError,
  login: async () => ({ ok: false, error: defaultConfigError }),
  register: async () => ({ ok: false, error: defaultConfigError }),
  logout: async () => {},
  sendResetPassword: async () => ({ ok: false, error: defaultConfigError }),
  updateProfile: async () => ({ ok: false, error: defaultConfigError }),
  updatePassword: async () => ({ ok: false, error: defaultConfigError }),
  refresh: async () => {},
});

export function DealerAuthProvider({ children }: { children: ReactNode }) {
  const [dealer, setDealer] = useState<DealerPortalUser | null>(null);
  const [loading, setLoading] = useState(true);
  const configError = defaultConfigError;

  const syncDealer = async () => {
    setLoading(true);

    try {
      const profile = await fetchCurrentDealerProfile();
      if (profile && profile.approved && profile.portalAccess) {
        setDealer(profile);
      } else {
        setDealer(null);
      }
    } catch {
      setDealer(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void syncDealer();
  }, []);

  const value = useMemo<DealerAuthContextValue>(
    () => ({
      dealer,
      isLoggedIn: Boolean(dealer),
      loading,
      configError,
      login: async (email, password) => {
        try {
          const result = await signInDealer(email, password);
          setDealer(result.dealer);
          return { ok: true };
        } catch (err) {
          return {
            ok: false,
            error: err instanceof Error ? err.message : 'Unable to sign in.',
          };
        }
      },
      register: async (input) => {
        try {
          await registerDealer(input);
          return {
            ok: true,
            message:
              'Application received. Our team will review it and enable portal access once approved.',
          };
        } catch (err) {
          return {
            ok: false,
            error: err instanceof Error ? err.message : 'Unable to submit your application.',
          };
        }
      },
      logout: async () => {
        await signOutCurrentUser();
        setDealer(null);
      },
      sendResetPassword: async (email) => {
        try {
          await sendPasswordReset(email);
          return {
            ok: true,
            message: 'Password reset email sent. Please check your inbox.',
          };
        } catch (err) {
          return {
            ok: false,
            error: err instanceof Error ? err.message : 'Unable to send password reset email.',
          };
        }
      },
      updateProfile: async (input) => {
        try {
          const nextDealer = await updateDealerProfile(input);
          if (nextDealer) {
            setDealer(nextDealer);
          }
          return { ok: true, message: 'Your account details were updated.' };
        } catch (err) {
          return {
            ok: false,
            error: err instanceof Error ? err.message : 'Unable to update your profile.',
          };
        }
      },
      updatePassword: async (newPassword) => {
        try {
          await updateCurrentUserPassword(newPassword);
          return { ok: true, message: 'Password updated successfully.' };
        } catch (err) {
          return {
            ok: false,
            error: err instanceof Error ? err.message : 'Unable to update your password.',
          };
        }
      },
      refresh: syncDealer,
    }),
    [configError, dealer, loading],
  );

  return (
    <DealerAuthContext.Provider value={value}>
      {children}
    </DealerAuthContext.Provider>
  );
}

export const useDealerAuth = () => useContext(DealerAuthContext);
